import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { map, switchMap, toArray } from 'rxjs/operators';
import { concat } from 'rxjs/internal/observable/concat';

import {
    ApiError,
    ApiResponse,
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    CompaniesService,
    IndividualsService,
    TradingPlatformService,
    IndividualInterface,
    CompanyModel,
    CompanyExtendedModel,
    IndividualExtendedModel,
    TradingPlatformInfoModel,
    TradingPlatformUserSubscriptionsInputModel,
    TradingPlatformVersionModel,
    TradingPlatformVersionPairModel,
    IndividualTradeSettingsInterface,
    IndividualTradeSettingsModel,
} from '@spa/api';

import { ArrayEveryValidator, InArrayValidator } from '@spa/validators';

import { BaseControl, Helper, SelectControl } from '../../../../shared/classes';
import { ComponentTypesEnum } from '../../../../shared/enums';

import { TradeSettingsFormErrorStatesEnum } from '../../enums';

import {
    TradeSettingsControls,
    TradeSettingsCategorySelector,
    TradeSettingsControlsInterface,
    TradeSettingsClientAliasSelector,
    TradeSettingsPlatformsVersionSelectorPrefix,
    TradeSettingsPlatformsSelector,
    TradeSettingsUseMarginLeverageSelector,
    TradeSettingsDailyBuyingPowerMultipleSelector,
    TradeSettingsOvernightBuyingPowerMultipleSelector, BuyingPowerMinimumValue
} from '../../../../../assets/descriptors';

export class TradeSettingsForm {
    private readonly individualsService: IndividualsService;
    private readonly companiesService: CompaniesService;
    private readonly client: IndividualExtendedModel | CompanyExtendedModel;
    private readonly tradingPlatformService: TradingPlatformService;
    private readonly allTradingPlatforms: TradingPlatformInfoModel[];
    private readonly subscribedTradingPlatforms: TradingPlatformVersionPairModel[];
    private readonly isEditableClientAlias: boolean;

    public form: FormGroup;
    public controls: TradeSettingsControlsInterface;

    constructor(
        individualsService: IndividualsService,
        companiesService: CompaniesService,
        client: IndividualExtendedModel | CompanyExtendedModel,
        tradingPlatformService: TradingPlatformService,
        allTradingPlatforms: TradingPlatformInfoModel[],
        subscribedTradingPlatforms: TradingPlatformVersionPairModel[],
        isEditableClientAlias: boolean
    ) {
        this.individualsService = individualsService;
        this.companiesService = companiesService;
        this.client = client;
        this.tradingPlatformService = tradingPlatformService;
        this.allTradingPlatforms = allTradingPlatforms;
        this.subscribedTradingPlatforms = subscribedTradingPlatforms;
        this.isEditableClientAlias = isEditableClientAlias;
    }

    private getClientAsCompany(): CompanyExtendedModel {
        return this.client as CompanyExtendedModel;
    }

    private fixCompanyModel(
        category: number,
        alias: string,
        useMargin: boolean,
        dailyBP?: number,
        overnightBP?: number): CompanyModel {
        const model = new CompanyModel();

        model.id = this.getClientAsCompany().id;
        model.name = this.getClientAsCompany().name;
        model.organizationType = this.getClientAsCompany().organizationType;
        model.dateOfIncorporation = this.getClientAsCompany().dateOfIncorporation;
        model.financialInstitution = this.getClientAsCompany().financialInstitution;
        model.miFIDCategory = this.getClientAsCompany().miFidCategory;
        model.enrollChannel = this.getClientAsCompany().enrollChannel;
        model.tradingCategory = category;
        model.tenantId = this.getClientAsCompany().tenantId;
        model.parentId = this.getClientAsCompany().parentId;
        model.countryOfIncorporationId = this.getClientAsCompany().countryOfIncorporation;
        model.lei = this.getClientAsCompany().lei;
        model.leiExpiration = this.getClientAsCompany().leiExpiration;
        model.vat = this.getClientAsCompany().vat;
        model.giin = this.getClientAsCompany().giin;
        model.alias = alias;
        model.referralCode = this.getClientAsCompany().referralCode;
        model.oldClientId = this.getClientAsCompany().oldClientId;
        model.useMarginLeverage = useMargin;
        model.dailyBuyingPowerMultiple = dailyBP;
        model.overnightBuyingPowerMultiple = overnightBP;
        return model;
    }

    private makeClientTradingPlatformSubscriptions(isCorporateClient: boolean, tradingPlatforms: TradingPlatformVersionPairModel[]): ServerResponse<boolean> {
        const tradingPlatformsBefore = tradingPlatforms.map(p=>p.tradingPlatformId ?? null);
        const tradingPlatformsAfter = this.subscribedTradingPlatforms.map(p=>p.tradingPlatformId ?? null);

        const tradingPlatformsVersionsBefore = tradingPlatforms.map(p=>p.tradingPlatformVersionId ?? null);
        const tradingPlatformsVersionsAfter = this.subscribedTradingPlatforms.map(p=>p.tradingPlatformVersionId ?? null);

        if (!Helper.compareSimpleArrays<number>(tradingPlatformsBefore, tradingPlatformsAfter)
            || !Helper.compareSimpleArrays<number>(tradingPlatformsVersionsBefore, tradingPlatformsVersionsAfter) ) {

            const model = new TradingPlatformUserSubscriptionsInputModel(
                tradingPlatforms,
                isCorporateClient,
                this.client.id
            );

            return this.tradingPlatformService.makeUsersSubscriptions(model)
                .pipe(
                    map((updateSubscriptionsResponse: ServerResponseData<null>) => {
                        if (updateSubscriptionsResponse.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse<boolean>(
                                updateSubscriptionsResponse,
                                TradeSettingsFormErrorStatesEnum.UpdatePlatformSubscriptionsFailed
                            );
                        }

                        this.subscribedTradingPlatforms.splice(0, this.subscribedTradingPlatforms.length);
                        this.subscribedTradingPlatforms.push(...tradingPlatforms);


                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }

    private updateClientTradingCategoryAndAlias(
        isCorporateClient: boolean,
        tradingCategory: number,
        alias: string,
        useMargin: boolean,
        dailyBP?: number,
        overnightBP?: number) {

        let shouldUpdate = tradingCategory !== this.client.tradingCategory ||
            (this.isEditableClientAlias && (alias !== null) && (alias !== this.client.alias)) ||
            useMargin !== this.client.useMarginLeverage ||
            dailyBP !== this.client.dailyBuyingPowerMultiple ||
            dailyBP !== this.client.overnightBuyingPowerMultiple;

        if (shouldUpdate) {
            if (!isCorporateClient) {

                return this.updateIndividual(tradingCategory, alias, useMargin, dailyBP, overnightBP);
            } else {

                return this.updateCompany(tradingCategory, alias, useMargin, dailyBP, overnightBP);
            }
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }

    private updateIndividual(
        tradingCategory: number,
        alias: string,
        useMargin: boolean,
        dailyBP?: number,
        overnightBP?: number
    ): ServerResponse<boolean> {
        return this.individualsService.getById(this.client.id).pipe(
            switchMap((getIndividualResponse: ServerResponseData<IndividualInterface>) => {
                if (getIndividualResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable<IndividualInterface>(getIndividualResponse,
                        TradeSettingsFormErrorStatesEnum.UpdateTradingCategoryFailed);
                }

                const data = getIndividualResponse.data as IndividualTradeSettingsInterface;
                const model = new IndividualTradeSettingsModel(data);

                model.tradingCategory = tradingCategory as number;
                model.alias = alias as string;
                model.useMarginLeverage = useMargin;
                model.dailyBuyingPowerMultiple = dailyBP;
                model.overnightBuyingPowerMultiple = overnightBP;

                return this.individualsService.updateIndividualTradeSettingsDetails(data.id, model)
                    .pipe(
                        map((updateIndividualResponse: ServerResponseData<null>) => {
                            if (updateIndividualResponse.status !== HttpStatusCodesEnum.Ok) {
                                return Helper.errorsHelper().createErrorResponse<null>(updateIndividualResponse,
                                    TradeSettingsFormErrorStatesEnum.UpdateTradingCategoryFailed);
                            }

                            this.client.tradingCategory = tradingCategory as number;
                            this.client.alias = alias as string;
                            this.client.useMarginLeverage = useMargin;
                            this.client.dailyBuyingPowerMultiple = dailyBP;
                            this.client.overnightBuyingPowerMultiple = overnightBP;

                            return Helper.createSuccessResponse<boolean>(true);
                        })
                    );
            })
        );
    }

    private updateCompany(
        tradingCategory: number,
        alias: string,
        useMargin: boolean,
        dailyBP?: number,
        overnightBP?: number): ServerResponse<boolean> {
        let model: CompanyModel = null;

        model = this.fixCompanyModel(tradingCategory, alias, useMargin, dailyBP, overnightBP);

        return this.companiesService.update(model)
            .pipe(
                map((updateCompanyResponse: ServerResponseData<null>) => {
                    if (updateCompanyResponse.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse<null>(updateCompanyResponse,
                            TradeSettingsFormErrorStatesEnum.UpdateTradingCategoryOrClientAliasFailed);
                    }
                    this.client.tradingCategory = tradingCategory as number;
                    this.client.alias = alias as string;
                    this.client.useMarginLeverage = useMargin;
                    this.client.dailyBuyingPowerMultiple = dailyBP;
                    this.client.overnightBuyingPowerMultiple = overnightBP;

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    bindMarginValidators(value: boolean): void {
        const controlDaily = this.controls.dailyBuyingPowerMultiple;
        const controlOvernight = this.controls.overnightBuyingPowerMultiple;

        const setValidators = (baseControl: BaseControl) => {
            const control = this.form.get(baseControl.name);
            control.clearValidators();

            if (value) {
                control.setValidators([Validators.required, Validators.min(1)]);
            }

            control.updateValueAndValidity();
        }

        setValidators(controlDaily);
        setValidators(controlOvernight);
    }

    bindVersionValidators(value: number[]): void {
        this.allTradingPlatforms.forEach((platform: TradingPlatformInfoModel) => {
            var versionSelector = this.controls.tradingPlatformVersions.find((c:SelectControl)=>c.name == TradeSettingsPlatformsVersionSelectorPrefix + platform.id.toString());
            if(!versionSelector){return;}
            const control = this.form.get(versionSelector.name);
            control.reset();
            control.setValue(null);
            control.clearValidators();
            if (value && value.indexOf(platform.id) >= 0) {
                var validators = [];
                var subscribedVersion = this.subscribedTradingPlatforms.find((c: TradingPlatformVersionPairModel) =>
                        c.tradingPlatformId == platform.id
                );
                control.setValue(subscribedVersion?.tradingPlatformVersionId);
                versionSelector.setHidden(false);
                const validatorIds = platform.trading_platform_versions.map((c: TradingPlatformVersionModel) => c.id).concat(null);
                validators.push(InArrayValidator(validatorIds));
                validators.push(Validators.required);
                control.setValidators(validators);
            }else{
                versionSelector.setHidden(true);
            }

            control.updateValueAndValidity();
        });
    }

    initForm(): void {
        this.form = new FormGroup({});
        this.controls = TradeSettingsControls();

        const allPlatformIds = [];
        const subscribedPlatformIds = this.subscribedTradingPlatforms.map((value:TradingPlatformVersionPairModel)=> value.tradingPlatformId);

        if (this.client) {
            this.controls.tradingCategory.value = this.client.tradingCategory;
            this.controls.clientAlias.value = this.client.alias;
            this.controls.useMarginLeverage.value = this.client.useMarginLeverage;
            this.controls.dailyBuyingPowerMultiple.value = this.client.dailyBuyingPowerMultiple;
            this.controls.overnightBuyingPowerMultiple.value = this.client.overnightBuyingPowerMultiple;
        }

        this.form.addControl(this.controls.tradingCategory.name, new FormControl(
            this.controls.tradingCategory.value,
            this.controls.tradingCategory.validators
        ));

        const marginControl = new FormControl(
            this.controls.useMarginLeverage.value,
            this.controls.useMarginLeverage.validators
        )

        marginControl.valueChanges.subscribe(value => {
            this.bindMarginValidators(value);
        })

        this.form.addControl(this.controls.useMarginLeverage.name, marginControl);

        this.form.addControl(this.controls.dailyBuyingPowerMultiple.name, new FormControl(
            this.controls.dailyBuyingPowerMultiple.value,
            this.controls.dailyBuyingPowerMultiple.validators
        ));

        this.form.addControl(this.controls.overnightBuyingPowerMultiple.name, new FormControl(
            this.controls.overnightBuyingPowerMultiple.value,
            this.controls.overnightBuyingPowerMultiple.validators
        ));


        if (this.isEditableClientAlias) {
            this.form.addControl(this.controls.clientAlias.name, new FormControl(
                this.controls.clientAlias.value,
                this.controls.clientAlias.validators
            ));
        } else {
            delete this.controls.clientAlias;
        }

        (this.controls.tradingPlatforms as SelectControl).value = !this.subscribedTradingPlatforms ? null : subscribedPlatformIds;

        (this.controls.tradingPlatforms as SelectControl).options = this.allTradingPlatforms.map((platform: TradingPlatformInfoModel) => {
             allPlatformIds.push(platform.id);
             return {label: platform.name, value: platform.id};
        });
        this.controls.tradingPlatforms.extendValidators(ArrayEveryValidator(allPlatformIds));
        const selectControl = new FormControl(
            this.controls.tradingPlatforms.value,
            this.controls.tradingPlatforms.validators
        );
        selectControl.valueChanges.subscribe(value => {
            this.bindVersionValidators(value);
        });
        this.form.addControl(this.controls.tradingPlatforms.name,selectControl);

        this.controls.tradingPlatformVersions = this.allTradingPlatforms.filter((p:TradingPlatformInfoModel)=>p.trading_platform_versions.length > 0)
            .map((platform: TradingPlatformInfoModel) => {
                const control = new SelectControl({
                    name: TradeSettingsPlatformsVersionSelectorPrefix + platform.id.toString(),
                    placeholder: platform.name + " Version",
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    value: null,
                    floatingLabelNever:true,
                    options: platform.trading_platform_versions.map((c:TradingPlatformVersionModel)=>{
                        return {label: c.name, value: c.id}
                    }),
                    multiple: false,
                });
                this.form.addControl(control.name, new FormControl(control.value, control.validators));
                return control;
            });

        this.bindVersionValidators((this.controls.tradingPlatforms as SelectControl).value);
        this.bindMarginValidators(this.controls.useMarginLeverage.value as boolean);
     }
    setAccountReferenceControlValidators(value: any) {
        throw new Error('Method not implemented.');
    }

    showErrors(messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }, control:BaseControl){
        const formControl = this.form.get(control.name);
        const minValue = BuyingPowerMinimumValue - 1;
        if (formControl && formControl.invalid)  {
            const errorMessages = Object.keys(formControl.errors).map((errorKey: string) => {
                return messageFunction(control.placeholder, minValue)[errorKey];
            });

            control.setErrors(errorMessages);
        }
    }

    validateForm(messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }): boolean {
        if (this.form.invalid) {
            Object.keys(this.controls).forEach((controlKey: string) => {
                if(controlKey == TradeSettingsPlatformsVersionSelectorPrefix){
                    this.controls.tradingPlatformVersions.forEach((c:BaseControl)=>{
                         this.showErrors(messageFunction, c);
                    });
                }else{
                    const control = this.controls[controlKey];
                    this.showErrors(messageFunction, control);
                }
            });

            return false;
        }

        return true;
    }

    createRequest(isCorporateClient: boolean): ServerResponse<boolean> {
        const tradingCategory = this.form.get(TradeSettingsCategorySelector).value;
        const useMarginLeverage = this.form.get(TradeSettingsUseMarginLeverageSelector).value;
        const dailyBp = this.form.get(TradeSettingsDailyBuyingPowerMultipleSelector).value;
        const overnightBp = this.form.get(TradeSettingsOvernightBuyingPowerMultipleSelector).value;
        const tradingPlatforms = this.form.get(TradeSettingsPlatformsSelector).value.map((i:number)=>{
            const versionControl = this.form.get(TradeSettingsPlatformsVersionSelectorPrefix + i)?.value;
            return new TradingPlatformVersionPairModel(i, versionControl);
        });

        const clientAlias = !this.form.contains(TradeSettingsClientAliasSelector) ? null :
            this.form.get(TradeSettingsClientAliasSelector).value;

        return concat(
            this.makeClientTradingPlatformSubscriptions(isCorporateClient, tradingPlatforms),
            this.updateClientTradingCategoryAndAlias(
                isCorporateClient,
                tradingCategory,
                clientAlias,
                useMarginLeverage,
                dailyBp,
                overnightBp),
        ).pipe(
            toArray(),
            map((responses: ServerResponseData<boolean>[]) => {
                if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
                    let errors: ApiError[] = [];
                    let messages: string[] = [];
                    responses.filter((response: ServerResponseData<boolean>) => {
                        return response.status !== HttpStatusCodesEnum.Ok;
                    }).forEach((response: ServerResponseData<boolean>) => {
                        errors = errors.concat(...response.data as ApiError[]);
                        if (response.messages.length > 0) {
                            messages = messages.concat(...response.messages);
                        }
                    });

                    return new ApiResponse(HttpStatusCodesEnum.BadRequest, errors, messages);
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }
}
